import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ClipboardList, Eye, ListFilter, RotateCcw, Search, TrendingUp } from 'lucide-react';
import { SectionHeader } from '../SectionHeader';
import { cn } from '@/lib/utils';

const RHYTHM_ICONS = {
  Assess: Search,
  Plan: ClipboardList,
  Prioritise: ListFilter,
  Act: TrendingUp,
  Review: Eye,
  Improve: RotateCcw,
};

const EASE = [0.22, 1, 0.36, 1];

// Section 3 — the Compass operating rhythm. Desktop: connected horizontal
// cycle with a detail panel; mobile: vertical step journey (all expanded).
export const CompassRhythm = ({ sp }) => {
  const steps = sp.rhythm || [];
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const current = steps[active] || steps[0];

  return (
    <section id="operating-rhythm" aria-labelledby="compass-rhythm-heading" data-testid="compass-rhythm" className="scroll-mt-28 bg-mist py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="03"
          eyebrow="How Compass Works"
          title="One connected rhythm from strategy to results"
          testid="compass-rhythm-header"
        />

        {/* Desktop: connected horizontal cycle */}
        <div className="mt-14 hidden md:block" data-testid="compass-rhythm-desktop">
          <ol className="relative mx-auto flex max-w-5xl items-start" role="list">
            <span className="absolute left-0 right-0 top-7 h-0.5 bg-slate-200" aria-hidden="true" />
            {steps.map((step, i) => {
              const Icon = RHYTHM_ICONS[step.title] || Search;
              const isActive = i === active;
              return (
                <li key={step.title} className="relative flex flex-1 flex-col items-center px-1">
                  <button
                    type="button"
                    aria-pressed={isActive}
                    aria-controls="compass-rhythm-panel"
                    data-testid={`compass-rhythm-${step.title.toLowerCase()}`}
                    onClick={() => setActive(i)}
                    className="group flex flex-col items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2"
                  >
                    <span
                      className={cn(
                        'relative z-10 flex h-14 w-14 items-center justify-center rounded-full border-2 bg-white transition-[background-color,border-color,color,box-shadow] duration-300',
                        isActive
                          ? 'border-navy-900 bg-navy-900 text-gold-500 shadow-[0_0_0_6px_rgba(255,184,0,0.25)]'
                          : 'border-slate-300 text-slate-400 group-hover:border-navy-300 group-hover:text-navy-600'
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className={cn('text-sm font-extrabold tracking-tight transition-colors', isActive ? 'text-navy-900' : 'text-slate-400 group-hover:text-navy-700')}>
                      {step.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <p className="mt-4 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-gold-700">
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            A continuous cycle — improve, then assess again
          </p>
          <div id="compass-rhythm-panel" aria-live="polite" className="mx-auto mt-8 max-w-2xl">
            <AnimatePresence mode="wait">
              <motion.p
                key={current?.title}
                initial={reduce ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="rounded-xl border border-slate-200 bg-white px-6 py-5 text-center text-sm leading-relaxed text-slate-600 shadow-card sm:text-base"
                data-testid="compass-rhythm-detail"
              >
                <span className="font-bold text-navy-900">{current?.title}. </span>
                {current?.text}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile: vertical step journey */}
        <ol className="relative mt-12 space-y-4 border-l-2 border-slate-200 pl-6 md:hidden" data-testid="compass-rhythm-mobile">
          {steps.map((step, i) => {
            const Icon = RHYTHM_ICONS[step.title] || Search;
            return (
              <li key={step.title} className="relative" data-testid={`compass-rhythm-m-${step.title.toLowerCase()}`}>
                <span className="absolute -left-[2.35rem] top-0 flex h-8 w-8 items-center justify-center rounded-full border-2 border-navy-900 bg-navy-900 text-gold-500" aria-hidden="true">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <h3 className="text-base font-extrabold tracking-tight text-navy-900">
                  <span className="mr-2 font-mono text-xs font-medium tracking-widest text-gold-700">{String(i + 1).padStart(2, '0')}</span>
                  {step.title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{step.text}</p>
              </li>
            );
          })}
          <li className="relative" aria-hidden="true">
            <p className="flex items-center gap-2 pt-1 text-xs font-bold uppercase tracking-widest text-gold-700">
              <RotateCcw className="h-3.5 w-3.5" /> Continuous — back to Assess
            </p>
          </li>
        </ol>
      </div>
    </section>
  );
};
